import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ImageOff, X } from 'lucide-react';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import ErrorState from '../../components/common/ErrorState';
import { Skeleton } from '../../components/common/Skeleton';
import { useToast } from '../../hooks/useToast';
import { fetchWreaths } from '../../utils/api';
import { WREATH_CATEGORIES } from '../../utils/constants';

const emptyForm = {
  name: '',
  price: '',
  category: '스탠다드',
  description: '',
  features: [],
  image: '',
  inStock: true,
};

const WreathFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToast();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [featureInput, setFeatureInput] = useState('');
  const [imageError, setImageError] = useState(false);

  const dirtyRef = useRef(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  const loadWreathData = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const wreaths = await fetchWreaths();
      const wreath = wreaths.find((w) => w.id === parseInt(id, 10));
      if (!wreath) {
        setNotFound(true);
        return;
      }
      setFormData({
        name: wreath.name || '',
        price: wreath.price || '',
        category: wreath.category || '스탠다드',
        description: wreath.description || '',
        features: Array.isArray(wreath.features) ? wreath.features : [],
        image: wreath.image || '',
        inStock: wreath.inStock !== false,
      });
    } catch (e) {
      console.error('화환 데이터 로드 실패:', e);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) loadWreathData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 작성 중 새로고침/창닫기 이탈 경고
  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const markDirty = () => {
    dirtyRef.current = true;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    markDirty();
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (name === 'image') setImageError(false);
  };

  const addFeature = () => {
    const f = featureInput.trim();
    if (!f) return;
    if (formData.features.includes(f)) {
      setFeatureInput('');
      return;
    }
    markDirty();
    setFormData((prev) => ({ ...prev, features: [...prev.features, f] }));
    setFeatureInput('');
  };

  const removeFeature = (f) => {
    markDirty();
    setFormData((prev) => ({ ...prev, features: prev.features.filter((x) => x !== f) }));
  };

  const handleFeatureKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addFeature();
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = '상품명을 입력해주세요.';
    if (formData.price === '' || formData.price === null) {
      next.price = '가격을 입력해주세요.';
    } else if (Number.isNaN(Number(formData.price))) {
      next.price = '가격은 숫자만 입력 가능합니다.';
    } else if (Number(formData.price) <= 0) {
      next.price = '가격은 0보다 커야 합니다.';
    }
    if (!formData.category) next.category = '카테고리를 선택해주세요.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      showToast('입력값을 확인해주세요.', 'error');
      return;
    }
    setSubmitting(true);
    // Mock 저장
    setTimeout(() => {
      dirtyRef.current = false;
      showToast(isEdit ? '화환 상품이 수정되었습니다.' : '화환 상품이 등록되었습니다.', 'success');
      setSubmitting(false);
      navigate('/admin/wreaths');
    }, 400);
  };

  const handleBack = () => {
    if (dirtyRef.current) {
      setLeaveOpen(true);
    } else {
      navigate('/admin/wreaths');
    }
  };

  if (notFound) {
    return (
      <div className="p-6">
        <ErrorState
          title="상품을 찾을 수 없습니다"
          message="존재하지 않거나 삭제된 상품입니다."
          onRetry={() => navigate('/admin/wreaths')}
          retryLabel="목록으로"
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 flex items-center gap-4">
          <button onClick={handleBack} className="text-gray-600 hover:text-gray-900">
            ← 돌아가기
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? '화환 상품 수정' : '화환 상품 등록'}
          </h1>
        </div>

        {loading ? (
          <div className="space-y-4 rounded-lg bg-white p-6 shadow">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 rounded-lg bg-white p-6 shadow" noValidate>
            <Input
              label="상품명"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="예: 백합 화환"
              error={errors.name}
              required
            />

            <Input
              label="가격"
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              placeholder="예: 120000"
              error={errors.price}
              required
            />

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                카테고리<span className="ml-1 text-red-600">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500"
                required
              >
                {WREATH_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">상품 설명</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500"
                placeholder="상품에 대한 설명을 입력하세요"
              />
            </div>

            {/* 특징 태그 칩 입력 */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">특징</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={featureInput}
                  onChange={(e) => setFeatureInput(e.target.value)}
                  onKeyDown={handleFeatureKeyDown}
                  placeholder="예: 백합 (Enter 또는 쉼표로 추가)"
                  className="flex-1 rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500"
                />
                <Button type="button" variant="outline" onClick={addFeature}>추가</Button>
              </div>
              {formData.features.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.features.map((f) => (
                    <span
                      key={f}
                      className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-sm text-primary-800"
                    >
                      {f}
                      <button
                        type="button"
                        onClick={() => removeFeature(f)}
                        aria-label={`${f} 제거`}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* 이미지 경로 + 미리보기 */}
            <div>
              <Input
                label="이미지 경로"
                type="text"
                name="image"
                value={formData.image}
                onChange={handleChange}
                placeholder="예: /images/wreaths/wreath-1.png"
              />
              {formData.image && (
                <div className="mt-3">
                  <p className="mb-1 text-xs text-gray-500">미리보기</p>
                  {imageError ? (
                    <div className="flex h-40 w-40 flex-col items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-gray-400">
                      <ImageOff className="h-8 w-8" />
                      <span className="mt-1 text-xs">이미지를 불러올 수 없습니다</span>
                    </div>
                  ) : (
                    <img
                      src={formData.image}
                      alt="미리보기"
                      onError={() => setImageError(true)}
                      className="h-40 w-40 rounded-md border border-gray-200 object-cover"
                    />
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="inStock"
                name="inStock"
                checked={formData.inStock}
                onChange={handleChange}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="inStock" className="ml-2 text-sm text-gray-700">재고 있음</label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" variant="primary" fullWidth disabled={submitting}>
                {submitting ? '저장 중...' : isEdit ? '수정하기' : '등록하기'}
              </Button>
              <Button type="button" variant="outline" onClick={handleBack} fullWidth disabled={submitting}>
                취소
              </Button>
            </div>
          </form>
        )}
      </div>

      <ConfirmModal
        isOpen={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        onConfirm={() => { dirtyRef.current = false; navigate('/admin/wreaths'); }}
        title="작성 취소"
        message="저장하지 않은 변경사항이 있습니다. 정말 나가시겠습니까?"
        confirmLabel="나가기"
        confirmVariant="danger"
      />
    </div>
  );
};

export default WreathFormPage;
